import {
  IApplicationFormData,
  IWizardCardReadiness,
  IWizardHouseholdEligibility,
  IWizardHouseholdEligibilityBlocker,
  IWizardHouseholdInviteStatus,
  IWizardHouseholdMemberStatus,
  IWizardReviewSummary,
  IWizardReviewCoApplicantStatus,
  IWizardSubmissionBlockReason,
  IWizardSubmissionGate,
  normalizeApplicationFormData,
} from '../models/application-form'
import {buildOtherOccupantChanges, readSavedOtherOccupants} from '../models/other-occupants'
import axios from 'axios'
import {IAddress, IPlaceDetails} from '../models/address'
import {FoundPlaceConfirmationRequest} from '../models/found-place'
import {
  CORRELATION_ID_HEADER,
  getOrCreateWizardCorrelationId,
} from '../../../shared/service/correlation-id'
import { read } from 'fs'

const JSON_HEADERS = {'Content-Type': 'application/json'}
const GUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
const API_URL = (process.env.REACT_APP_API_URL || '/api').replace(/\/+$/, '')
const API_BASE = `${API_URL}/wizard`
const MIN_ADDRESS_SEARCH_LENGTH = 3
const WIZARD_APPLICATION_TYPES = new Set<NonNullable<IApplicationFormData['applicationType']>>([
  'Individual',
  'Couple',
  'Housemates',
  'Family',
  'CompanyTrust',
])

const placeDetailsCache = new Map<string, IPlaceDetails>()
const streetViewCache = new Map<string, string>()

type WizardDraftDto = {
  applicationId?: number
  sessionId?: string
  blockNotVerifiedCoApplicant?: boolean
  stepKey?: string
  status?: string
  renterRole?: string
  fullName?: string
  firstName?: string
  lastName?: string
  mobileNumber?: string
  australianStatus?: string
  email?: string
  dateOfBirth?: string | Date | null
  termsAccepted?: boolean
  consentSharingAccepted?: boolean
  isIdentityVerified?: boolean
  isMobileVerified?: boolean
  mobileVerifiedAtUtc?: string
  lastSavedAtUtc?: string
  applicationType?: string
  companyTrustAbnAcn?: string
  companyTrustDirectorId?: string
  companyTrustLetterOfAuthorityDocumentId?: string
  emergencyContactName?: string
  emergencyContactPhone?: string
  emergencyContactRelationship?: string
  moveInAsap?: boolean
  moveInPreferredDate?: string
  preferredLeaseTerm?: string
  rentWeeklyBudgetMin?: number
  rentWeeklyBudgetMax?: number
  previousRentalHistory?: boolean
  previousPropertyManager?: string
  previousPropertyManagerPhone?: string
  previousRentWeekly?: number
  previousRentDuration?: string
  rentalLedgerUploadLater?: boolean
  rentalHistoryReasonForLeaving?: string
  bondFullyRefundedAtLastAddress?: boolean
  identificationType?: string
  isVisaHolder?: boolean
  isInternationalStudent?: boolean
  incomeUploadLaterTypes?: string[]
  retentionPreference?: number
  retentionSelectedAtUtc?: string
  auditTransparencyAcknowledged?: boolean
  coApplicants?: IApplicationFormData['coApplicants']
  employment?: IApplicationFormData['employment']
  income?: IApplicationFormData['income']
  employments?: Array<
    NonNullable<IApplicationFormData['employment']> & NonNullable<IApplicationFormData['income']>
  >
  pets?: IApplicationFormData['pets']
  otherOccupants?: IApplicationFormData['otherOccupants']
  otherOccupantChanges?: {
    upserts: IApplicationFormData['otherOccupants']
    removals: IApplicationFormData['otherOccupants']
  }
  vehicles?: IApplicationFormData['vehicles']
  hasPets?: boolean
  hasVehicles?: boolean
  hasOtherOccupants?: boolean
  documents?: IApplicationFormData['documents']
}
type SaveDraftPayload = {
  draft: WizardDraftDto
  isAutosave: boolean
}

type SaveDraftResult = {
  applicationId: number
  sessionId: string
  savedAtUtc: string
  status: string
  pets: IApplicationFormData['pets']
  vehicles: IApplicationFormData['vehicles']
  otherOccupants: IApplicationFormData['otherOccupants']
}

const occupantSavesBySession = new Map<string, Promise<SaveDraftResult>>()

type LoadDraftResult = {
  found: boolean
  draft?: WizardDraftDto
  primaryApplicant?: WizardDraftDto
}

type VerifyMobileResult = {
  exists: boolean
  existingApplicationId?: number
}

type SendOtpResult = {
  sent: boolean
  expiresAtUtc?: string
  message?: string
  retryAfterSeconds?: number
  lockedUntilUtc?: string
  isLockedOut?: boolean
}

type VerifyOtpResult = {
  verified: boolean
  sessionId?: string
  verifiedAtUtc?: string
  failureCode?: string
  retryAfterSeconds?: number
  lockedUntilUtc?: string
  isLockedOut?: boolean
  requiresNewCode?: boolean
}

type CoApplicantInviteResult = {
  inviteId: string
  fullName: string
  mobileNumber: string
  email: string
  role: string
  status: string
  isMobileVerified: boolean
  invitedAtUtc?: string
  inviteExpiresAtUtc?: string
  verifiedAtUtc?: string
  revokedAtUtc?: string
  declinedAtUtc?: string
  declineReason?: string
  relationship?: string
}

type CoApplicantInviteActionResult = {
  success: boolean
  leaveEventId?: string
  processingState?: string
  invite?: CoApplicantInviteResult
  message?: string
  retryAfterSeconds?: number
  lockedUntilUtc?: string
  isLockedOut?: boolean
  requiresNewCode?: boolean
}

type VerifyCoApplicantInviteResult = {
  verified: boolean
  invite?: CoApplicantInviteResult
  failureCode?: string
  retryAfterSeconds?: number
  lockedUntilUtc?: string
  isLockedOut?: boolean
  requiresNewCode?: boolean
  sessionId?: string
  applicationId?: number
  nextStepPath?: string
}

type CoApplicantInviteStatusResult = {
  found: boolean
  isVerified: boolean
  isExpired: boolean
  isDeclined?: boolean
  declinedAtUtc?: string
  status?: string
  primaryApplicant?: IApplicationFormData
  inviterApplicant?: IApplicationFormData
  invite?: CoApplicantInviteResult
}

type UploadWizardDocumentResponse = {
  success: boolean
  document?: IApplicationFormData['documents'][number]
  message?: string
}

type FinalizeSubmissionLinkResult = {
  submissionId: string
  secureViewUrl: string
  sentAtUtc?: string
  expiresAtUtc?: string
  status?: string
  pmEmail?: string
}

export type SubmissionBlockReasonResult = IWizardSubmissionBlockReason

type FinalizeDooraCardResult = {
  dooraCardId: number
  sessionId: string
  isBlocked?: boolean
  message?: string
}

type FinalizeSubmissionResult = {
  isBlocked?: boolean
  blockCode?: string
  message?: string
  blockReasons?: SubmissionBlockReasonResult[]
  submissionId: string
  token: string
  secureViewUrl: string
  expiresAtUtc?: string
  pmEmail?: string
  links?: FinalizeSubmissionLinkResult[]
}

export type SubmitToPropertyResult = {
  isBlocked?: boolean
  blockCode?: string
  message?: string
  blockReasons?: SubmissionBlockReasonResult[]
  submissionId: string
  token: string
  secureViewUrl: string
  expiresAtUtc?: string
  status: string
  sentVia: 'email' | 'sms' | 'admin_email' | 'multiple' | 'none'
  agentEmail?: string
  agents?: SubmitToPropertyAgentResult[]
  links?: FinalizeSubmissionLinkResult[]
  deliverySucceeded: boolean
  deliveryWarning?: string
}

export type SubmitToPropertyAgentResult = {
  agentName: string
  agentPhone: string
  agentEmail?: string
  sentVia: 'email' | 'sms' | 'admin_email' | 'none'
  status: string
  deliverySucceeded: boolean
  deliveryWarning?: string
}

export type RevokePropertySubmissionResult = {
  success: boolean
  message?: string
  status?: string
  revokedAtUtc?: string
}

export type SecureSubmissionViewResult = {
  submissionId: string
  viewedAtUtc?: string
  expiresAtUtc?: string
  propertyAddress?: string
  snapshot: unknown
}

export type SecureSubmissionDocumentResult = {
  blob: Blob
  contentType: string
}

export type SecurePreviewSessionResult = {
  previewToken: string
  expiresAtUtc?: string
}

export class WizardApiError extends Error {
  readonly code?: string
  readonly status?: number
  readonly redirectUrl?: string
  readonly retryAfterSeconds?: number
  readonly isBlocked: boolean
  readonly blockCode?: string
  readonly blockReasons: SubmissionBlockReasonResult[]

  constructor(
    message: string,
    options?: {
      code?: string
      status?: number
      redirectUrl?: string
      retryAfterSeconds?: number
      isBlocked?: boolean
      blockCode?: string
      blockReasons?: SubmissionBlockReasonResult[]
    }
  ) {
    super(message)
    this.name = 'WizardApiError'
    this.code = options?.code
    this.status = options?.status
    this.redirectUrl = options?.redirectUrl
    this.retryAfterSeconds = options?.retryAfterSeconds
    this.isBlocked = options?.isBlocked ?? false
    this.blockCode = options?.blockCode
    this.blockReasons = options?.blockReasons ?? []
  }
}

export type PropertySubmissionItem = {
  id: string
  canSelectForFoundPlace: boolean
  title: string
  address: string
  submittedAt?: string
  agentName: string
  agentPhone: string
  agentEmail?: string
  agents?: PropertySubmissionAgentItem[]
  status: string
  photoClassName: string
  viewedBy?: string
  viewedAt?: string
  expiresAt?: string
  revokedAt?: string
  expiresInDays: number
  placeId?: string
  applicationId?: number
  applicationFullName?: string
  submittedByName?: string
  includedMemberCount?: number
  isHouseholdCompositionOutdated?: boolean
  householdCompositionChangeType?: 'none' | 'added' | 'removed' | 'changed'
  addedHouseholdMemberNames?: string[]
  removedHouseholdMemberNames?: string[]
  currentHouseholdMemberCount?: number
  submittedHouseholdMemberCount?: number
}

export type PropertySubmissionAgentItem = {
  name: string
  phone: string
  email?: string
  status: string
  deliveryChannel?: string
  deliveryWarning?: string
  sentAt?: string
  viewedAt?: string
  isPrimary: boolean
}

export type SubmissionEnterEmailContext = {
  applicantName?: string
  propertyAddress?: string
  agentName?: string
  agentPhone?: string
  emailAlreadyProvided: boolean
}

export type SubmitPmEmailResult = {
  success: boolean
  message?: string
  secureViewUrl?: string
}

type PlacePrediction = {
  placeId: string
  description: string
}

type StreetViewOptions = {
  heading?: number
  pitch?: number
  fov?: number
  width?: number
  height?: number
}

const toOptionalNumber = (value: unknown): number | undefined => {
  if (value === null || value === undefined || value === '') {
    return undefined
  }

  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : undefined
}

const toOptionalBoolean = (value: unknown): boolean | undefined => {
  if (value === true || value === false) {
    return value
  }

  if (typeof value !== 'string') {
    return undefined
  }

  const normalized = value.trim().toLowerCase()
  if (normalized === 'true') {
    return true
  }

  if (normalized === 'false') {
    return false
  }

  return undefined
}

const toOptionalPositiveInteger = (value: unknown): number | undefined => {
  const parsed = toOptionalNumber(value)
  if (parsed === undefined) {
    return undefined
  }

  return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined
}

const toOptionalGuid = (value: unknown): string | undefined => {
  if (typeof value !== 'string') {
    return undefined
  }

  const normalized = value.trim()
  if (!normalized) {
    return undefined
  }

  return GUID_REGEX.test(normalized) ? normalized : undefined
}

const toWizardApplicationType = (value?: string): IApplicationFormData['applicationType'] => {
  const normalized = value?.trim()

  return WIZARD_APPLICATION_TYPES.has(
    normalized as NonNullable<IApplicationFormData['applicationType']>
  )
    ? (normalized as NonNullable<IApplicationFormData['applicationType']>)
    : undefined
}

const DATE_INPUT_VALUE_REGEX = /^\d{4}-\d{2}-\d{2}$/

const formatDateInputValue = (date: Date): string => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const toDateInputValue = (value: unknown): string | null => {
  if (value === null || value === undefined || value === '') {
    return null
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : formatDateInputValue(value)
  }

  if (typeof value === 'string') {
    const normalized = value.trim()
    if (!normalized) {
      return null
    }

    if (DATE_INPUT_VALUE_REGEX.test(normalized)) {
      return normalized
    }

    const datePart = normalized.slice(0, 10)
    if (DATE_INPUT_VALUE_REGEX.test(datePart)) {
      return datePart
    }

    const parsed = new Date(normalized)
    return Number.isNaN(parsed.getTime()) ? null : formatDateInputValue(parsed)
  }

  return null
}

const normalizeOtherOccupantsForDraft = (
  otherOccupants?: IApplicationFormData['otherOccupants']
): IApplicationFormData['otherOccupants'] =>
  (otherOccupants ?? [])
    .filter(
      (occupant) =>
        !!occupant.name?.trim() ||
        !!occupant.relationship?.trim() ||
        toOptionalNumber(occupant.age) !== undefined
    )
    .map((occupant) => ({
      id: occupant.id,
      clientId: occupant.clientId,
      name: occupant.name ?? '',
      age: toOptionalNumber(occupant.age),
      relationship: occupant.relationship ?? '',
    }))

const buildDraftPayload = (data: IApplicationFormData, stepKey?: string): WizardDraftDto => ({
  applicationId: data.applicationId,
  sessionId: data.sessionId,
  stepKey: stepKey ?? data.stepKey,
  status: data.status ?? 'draft',
  renterRole: data.renterRole,
  fullName: data.fullName,
  firstName: data.firstName,
  lastName: data.lastName,
  mobileNumber: data.mobileNumber,
  email: data.email,
  australianStatus: data.australianStatus,
  dateOfBirth: data.dateOfBirth,
  termsAccepted: data.termsAccepted,
  consentSharingAccepted: data.consentSharingAccepted,
  isIdentityVerified: data.isIdentityVerified,
  isMobileVerified: data.isMobileVerified,
  mobileVerifiedAtUtc: data.mobileVerifiedAtUtc,
  applicationType: data.applicationType,
  companyTrustAbnAcn: data.companyTrustAbnAcn,
  companyTrustDirectorId: data.companyTrustDirectorId,
  companyTrustLetterOfAuthorityDocumentId: toOptionalGuid(
    data.companyTrustLetterOfAuthorityDocumentId
  ),
  emergencyContactName: data.emergencyContactName,
  emergencyContactPhone: data.emergencyContactPhone,
  emergencyContactRelationship: data.emergencyContactRelationship,
  moveInAsap: toOptionalBoolean(data.moveInAsap),
  moveInPreferredDate:
    toOptionalBoolean(data.moveInAsap) === true
      ? undefined
      : toDateInputValue(data.moveInPreferredDate) ?? undefined,
  preferredLeaseTerm: data.preferredLeaseTerm,
  rentWeeklyBudgetMin: toOptionalNumber(data.rentWeeklyBudgetMin),
  rentWeeklyBudgetMax: toOptionalNumber(data.rentWeeklyBudgetMax),
  previousRentalHistory: data.previousRentalHistory,
  previousPropertyManager: data.previousPropertyManager,
  previousPropertyManagerPhone: data.previousPropertyManagerPhone,
  previousRentWeekly: toOptionalNumber(data.previousRentWeekly),
  previousRentDuration: data.previousRentDuration,
  rentalLedgerUploadLater: data.rentalLedgerUploadLater,
  rentalHistoryReasonForLeaving: data.rentalHistoryReasonForLeaving,
  bondFullyRefundedAtLastAddress: data.bondFullyRefundedAtLastAddress,
  retentionPreference: toOptionalNumber(data.retentionPreference),
  auditTransparencyAcknowledged: data.auditTransparencyAcknowledged,
  identificationType: data.identificationType,
  isVisaHolder: toOptionalBoolean(data.isVisaHolder),
  isInternationalStudent: toOptionalBoolean(data.isInternationalStudent),
  incomeUploadLaterTypes: data.incomeUploadLaterTypes ?? [],
  coApplicants: data.coApplicants ?? [],
  employment: data.employment
    ? {
        ...data.employment,
        employmentId: toOptionalPositiveInteger(data.employment.employmentId),
        startDate: toDateInputValue(data.employment.startDate),
      }
    : undefined,
  income: data.income
    ? {
        ...data.income,
        income: toOptionalNumber(data.income.income),
        selfEmploymentIncome: toOptionalNumber(data.income.selfEmploymentIncome),
        centreAmount: toOptionalNumber(data.income.centreAmount),
        pensionAmount: toOptionalNumber(data.income.pensionAmount),
        ndisAmount: toOptionalNumber(data.income.ndisAmount),
        studentSupportAmount: toOptionalNumber(data.income.studentSupportAmount),
      }
    : undefined,
  pets: data.pets ?? [],
  vehicles: data.vehicles ?? [],
  otherOccupants:
    data.hasOtherOccupants === false ? [] : normalizeOtherOccupantsForDraft(data.otherOccupants),
  otherOccupantChanges:
    (stepKey ?? data.stepKey) === 'other-occupants'
      ? {
          upserts: normalizeOtherOccupantsForDraft(buildOtherOccupantChanges(data).upserts),
          removals: buildOtherOccupantChanges(data).removals.map(({id, clientId}) => ({id, clientId})),
        }
      : undefined,
  hasPets: data.pets.length > 0,
  hasVehicles: data.vehicles.length > 0,
  hasOtherOccupants:
    data.hasOtherOccupants !== false && normalizeOtherOccupantsForDraft(data.otherOccupants).length > 0,
  documents: (data.documents ?? []).map((document) => ({
    ...document,
    documentId: toOptionalGuid(document.documentId),
  })),
})

const mapDraftToForm = (draft: WizardDraftDto): IApplicationFormData =>
  normalizeApplicationFormData({
    applicationId: draft.applicationId,
    blockNotVerifiedCoApplicant: draft.blockNotVerifiedCoApplicant,
    sessionId: draft.sessionId,
    stepKey: draft.stepKey,
    status: draft.status === 'submitted' ? 'submitted' : 'draft',
    renterRole: (draft.renterRole as IApplicationFormData['renterRole']) ?? 'primary',
    fullName: draft.fullName,
    firstName: draft.firstName,
    lastName: draft.lastName,
    mobileNumber: draft.mobileNumber,
    email: draft.email,
    australianStatus: draft.australianStatus,
    dateOfBirth: toDateInputValue(draft.dateOfBirth),
    termsAccepted: draft.termsAccepted,
    consentSharingAccepted: draft.consentSharingAccepted,
    isIdentityVerified: draft.isIdentityVerified,
    isMobileVerified: draft.isMobileVerified,
    mobileVerifiedAtUtc: draft.mobileVerifiedAtUtc,
    lastSavedAtUtc: draft.lastSavedAtUtc,
    applicationType: toWizardApplicationType(draft.applicationType),
    companyTrustAbnAcn: draft.companyTrustAbnAcn,
    companyTrustDirectorId: draft.companyTrustDirectorId,
    companyTrustLetterOfAuthorityDocumentId: draft.companyTrustLetterOfAuthorityDocumentId,
    emergencyContactName: draft.emergencyContactName,
    emergencyContactPhone: draft.emergencyContactPhone,
    emergencyContactRelationship: draft.emergencyContactRelationship,
    moveInAsap: toOptionalBoolean(draft.moveInAsap),
    moveInPreferredDate: toDateInputValue(draft.moveInPreferredDate) ?? undefined,
    preferredLeaseTerm: draft.preferredLeaseTerm,
    rentWeeklyBudgetMin: draft.rentWeeklyBudgetMin,
    rentWeeklyBudgetMax: draft.rentWeeklyBudgetMax,
    previousRentalHistory: draft.previousRentalHistory,
    previousPropertyManager: draft.previousPropertyManager,
    previousPropertyManagerPhone: draft.previousPropertyManagerPhone,
    previousRentWeekly: draft.previousRentWeekly,
    previousRentDuration: draft.previousRentDuration,
    rentalLedgerUploadLater: draft.rentalLedgerUploadLater,
    rentalHistoryReasonForLeaving: draft.rentalHistoryReasonForLeaving,
    bondFullyRefundedAtLastAddress: draft.bondFullyRefundedAtLastAddress,
    retentionPreference: draft.retentionPreference,
    retentionSelectedAtUtc: draft.retentionSelectedAtUtc,
    auditTransparencyAcknowledged: draft.auditTransparencyAcknowledged,
    identificationType: draft.identificationType,
    isVisaHolder: toOptionalBoolean(draft.isVisaHolder),
    isInternationalStudent: toOptionalBoolean(draft.isInternationalStudent),
    incomeUploadLaterTypes: draft.incomeUploadLaterTypes ?? [],
    coApplicants: draft.coApplicants ?? [],
    employment: draft.employment
      ? {
          ...draft.employment,
          employmentId: toOptionalPositiveInteger(draft.employment.employmentId),
          startDate: toDateInputValue(draft.employment.startDate),
        }
      : draft.employments?.[0]
      ? {
          ...draft.employments[0],
          employmentId: toOptionalPositiveInteger(draft.employments[0].employmentId),
          startDate: toDateInputValue(draft.employments[0].startDate),
        }
      : undefined,
    income: draft.income ?? draft.employments?.[0],
    pets: draft.pets ?? [],
    vehicles: draft.vehicles ?? [],
    otherOccupants: draft.otherOccupants ?? [],
    otherOccupantBaseline: (draft.otherOccupants ?? []).map((item) => ({...item})),
    removedOtherOccupants: [],
    hasPets: draft.pets ? draft.pets.length > 0 : false,
    hasVehicles: draft.vehicles ? draft.vehicles.length > 0 : false,
    hasOtherOccupants: draft.otherOccupants ? draft.otherOccupants.length > 0 : false,
    documents: draft.documents ?? [],
  })

type ApiRecord = Record<string, any>

const toApiRecord = (value: unknown): ApiRecord =>
  value && typeof value === 'object' ? (value as ApiRecord) : {}

const readApiArray = (value: unknown): ApiRecord[] =>
  Array.isArray(value) ? value.map(toApiRecord) : []

const normalizeStringArray = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
    : []

const normalizeSubmissionBlockReasons = (value: unknown): SubmissionBlockReasonResult[] =>
  readApiArray(value).map((reason) => ({
    ...reason,
    householdInviteId: reason.householdInviteId,
    householdMemberId: reason.householdMemberId,
    dooraCardId: toOptionalNumber(reason.dooraCardId),
    role: reason.role,
    inviteStatus: reason.inviteStatus,
    membershipStatus: reason.membershipStatus,
    fullName: reason.fullName,
    mobileNumber: reason.mobileNumber,
    isMobileVerified: reason.isMobileVerified,
    mobileVerifiedAtUtc: reason.mobileVerifiedAtUtc,
    isCardComplete: reason.isCardComplete,
    cardCompletedAtUtc: reason.cardCompletedAtUtc,
    reasonCodes: normalizeStringArray(reason.reasonCodes),
    reasonMessages: normalizeStringArray(reason.reasonMessages),
  }))

const normalizeSubmissionGate = (value: unknown): IWizardSubmissionGate | undefined => {
  const gate = toApiRecord(value)
  if (Object.keys(gate).length === 0) {
    return undefined
  }

  return {
    ...gate,
    isBlocked: gate.isBlocked,
    blockCode: gate.blockCode,
    message: gate.message,
    includedHouseholdMemberIds: normalizeStringArray(gate.includedHouseholdMemberIds),
    blockReasons: normalizeSubmissionBlockReasons(gate.blockReasons),
  }
}

const normalizeCardReadiness = (value: unknown): IWizardCardReadiness | undefined => {
  const readiness = toApiRecord(value)
  if (typeof readiness.isComplete !== 'boolean' || !Array.isArray(readiness.missingRequirementCodes)) {
    return undefined
  }

  const documents = toApiRecord(readiness.documentReadiness)
  return {
    isComplete: readiness.isComplete === true,
    canFinalize:
      typeof readiness.canFinalize === 'boolean' ? readiness.canFinalize : readiness.isComplete === true,
    missingRequirementCodes: normalizeStringArray(readiness.missingRequirementCodes),
    missingRequirementMessages: normalizeStringArray(readiness.missingRequirementMessages),
    documentReadiness:
      typeof documents.isIdentificationTypeSelected === 'boolean' &&
      Array.isArray(documents.requiredTypes) && Array.isArray(documents.missingTypes)
        ? {
            isIdentificationTypeSelected: documents.isIdentificationTypeSelected,
            requiredTypes: normalizeStringArray(documents.requiredTypes),
            missingTypes: normalizeStringArray(documents.missingTypes),
            deferredTypes: normalizeStringArray(documents.deferredTypes),
          }
        : undefined,
  }
}

const normalizeHouseholdEligibilityBlockers = (
  value: unknown
): IWizardHouseholdEligibilityBlocker[] =>
  readApiArray(value).map((blocker) => ({
    code: typeof blocker.code === 'string' ? blocker.code : '',
    message: typeof blocker.message === 'string' ? blocker.message : '',
    householdMemberId: blocker.householdMemberId,
    householdInviteId: blocker.householdInviteId,
  }))

const normalizeHouseholdEligibility = (value: unknown): IWizardHouseholdEligibility | undefined => {
  const eligibility = toApiRecord(value)
  if (Object.keys(eligibility).length === 0) {
    return undefined
  }

  return {
    canSubmit: eligibility.canSubmit === true,
    blockingReasons: normalizeHouseholdEligibilityBlockers(eligibility.blockingReasons),
    warnings: readApiArray(eligibility.warnings).map((warning) => ({
      code: typeof warning.code === 'string' ? warning.code : '',
      message: typeof warning.message === 'string' ? warning.message : '',
      householdInviteId: warning.householdInviteId,
    })),
  }
}

const normalizeReviewCoApplicantStatuses = (value: unknown): IWizardReviewCoApplicantStatus[] =>
  readApiArray(value).map((item) => ({
    ...item,
    inviteId: item.inviteId,
    fullName: item.fullName,
    mobileNumber: item.mobileNumber,
    role: item.role,
    status: item.status,
    isMobileVerified: item.isMobileVerified,
    coApplicantApplicationId: toOptionalNumber(item.coApplicantApplicationId),
    hasLinkedApplication: item.hasLinkedApplication,
    isCompleted: item.isCompleted,
    invitedAtUtc: item.invitedAtUtc,
    inviteExpiresAtUtc: item.inviteExpiresAtUtc,
    otpExpiresAt: item.otpExpiresAt,
    declinedAtUtc: item.declinedAtUtc,
    declineReason: item.declineReason,
    missingRequiredFields: normalizeStringArray(item.missingRequiredFields),
  }))

const normalizeHouseholdMembers = (value: unknown): IWizardHouseholdMemberStatus[] =>
  readApiArray(value).map((item) => ({
    ...item,
    householdMemberId: item.householdMemberId,
    dooraCardId: toOptionalNumber(item.dooraCardId),
    applicationId: toOptionalNumber(item.applicationId),
    fullName: item.fullName ?? item.applicantName,
    mobileNumber: item.mobileNumber,
    email: item.email,
    role: item.role,
    membershipStatus: item.membershipStatus,
    isMobileVerified: item.isMobileVerified,
    mobileVerifiedAtUtc: item.mobileVerifiedAtUtc,
    isCardComplete: item.isCardComplete ?? item.cardComplete,
    cardCompletedAtUtc: item.cardCompletedAtUtc,
    canSubmitHousehold: item.canSubmitHousehold,
    otpExpiresAt: item.otpExpiresAt,
    declinedAtUtc: item.declinedAtUtc,
    declineReason: item.declineReason,
    missingRequiredFields: normalizeStringArray(item.missingRequiredFields),
  }))

const normalizeHouseholdInvites = (value: unknown): IWizardHouseholdInviteStatus[] =>
  readApiArray(value).map((item) => ({
    ...item,
    inviteId: item.inviteId ?? item.householdInviteId,
    householdInviteId: item.householdInviteId,
    fullName: item.fullName ?? item.invitedFullName,
    mobileNumber: item.mobileNumber ?? item.invitedMobileNumber,
    email: item.email ?? item.invitedEmail,
    role: item.role,
    status: item.status ?? item.inviteStatus,
    inviteStatus: item.inviteStatus,
    isMobileVerified: item.isMobileVerified,
    invitedAtUtc: item.invitedAtUtc,
    inviteExpiresAtUtc: item.inviteExpiresAtUtc,
    verifiedAtUtc: item.verifiedAtUtc,
    otpExpiresAt: item.otpExpiresAt,
    declinedAtUtc: item.declinedAtUtc,
    declineReason: item.declineReason,
    relationship: item.relationship,
    canResend: item.canResend,
    missingRequiredFields: normalizeStringArray(item.missingRequiredFields),
  }))

const normalizeReviewSummaryPayload = (payload: unknown): IWizardReviewSummary => {
  const body = toApiRecord(payload)
  const gate = normalizeSubmissionGate(body.submissionGate)
  const cardReadiness = normalizeCardReadiness(body.cardReadiness)
  const householdEligibility = normalizeHouseholdEligibility(body.householdEligibility)

  return {
    ...body,
    applicationId: toOptionalNumber(body.applicationId),
    applicationContextId: toOptionalNumber(body.applicationContextId),
    householdId: toOptionalNumber(body.householdId),
    sessionId: body.sessionId,
    applicantName: body.applicantName,
    mobileNumber: body.mobileNumber,
    email: body.email,
    applicationType: body.applicationType,
    status: body.status,
    hasDashboardAccess: body.hasDashboardAccess === true,
    isMobileVerified: body.isMobileVerified,
    lastSavedAtUtc: body.lastSavedAtUtc,
    coApplicantCount: toOptionalNumber(body.coApplicantCount),
    employmentCount: toOptionalNumber(body.employmentCount),
    petCount: toOptionalNumber(body.petCount),
    vehicleCount: toOptionalNumber(body.vehicleCount),
    documentCount: toOptionalNumber(body.documentCount),
    incompleteUploadCount: toOptionalNumber(body.incompleteUploadCount),
    otherOccupantCount: toOptionalNumber(body.otherOccupantCount),
    areRequiredCoApplicantsComplete: body.areRequiredCoApplicantsComplete,
    renterRole: body.renterRole,
    missingCriticalFields: normalizeStringArray(body.missingCriticalFields),
    coApplicantStatuses: normalizeReviewCoApplicantStatuses(body.coApplicantStatuses),
    activeMembers: normalizeHouseholdMembers(body.activeMembers),
    pendingInvites: normalizeHouseholdInvites(body.pendingInvites),
    submissionGate: gate,
    cardReadiness,
    householdEligibility,
  }
}

const normalizeSubmissionLinks = (value: unknown): FinalizeSubmissionLinkResult[] =>
  readApiArray(value).map((item) => ({
    submissionId: item.submissionId ?? '',
    secureViewUrl: item.secureViewUrl ?? '',
    sentAtUtc: item.sentAtUtc,
    expiresAtUtc: item.expiresAtUtc,
    status: item.status,
    pmEmail: item.pmEmail,
  }))

const normalizeSubmitToPropertyAgents = (value: unknown): SubmitToPropertyAgentResult[] =>
  readApiArray(value).map((item) => ({
    agentName: item.agentName ?? '',
    agentPhone: item.agentPhone ?? '',
    agentEmail: item.agentEmail,
    sentVia: item.sentVia ?? 'none',
    status: item.status ?? '',
    deliverySucceeded: item.deliverySucceeded ?? false,
    deliveryWarning: item.deliveryWarning,
  }))

const normalizeSubmitToPropertyResult = (payload: unknown): SubmitToPropertyResult => {
  const body = toApiRecord(payload)
  const blockReasons = normalizeSubmissionBlockReasons(body.blockReasons)

  return {
    ...body,
    isBlocked: body.isBlocked,
    blockCode: body.blockCode ?? body.errorCode,
    message: body.message,
    blockReasons,
    submissionId: body.submissionId ?? '',
    token: body.token ?? '',
    secureViewUrl: body.secureViewUrl ?? '',
    expiresAtUtc: body.expiresAtUtc,
    status: body.status ?? '',
    sentVia: body.sentVia ?? 'none',
    agentEmail: body.agentEmail,
    agents: normalizeSubmitToPropertyAgents(body.agents),
    links: normalizeSubmissionLinks(body.links),
    deliverySucceeded: body.deliverySucceeded ?? false,
    deliveryWarning: body.deliveryWarning,
  }
}

const normalizePropertySubmissionAgents = (value: unknown): PropertySubmissionAgentItem[] =>
  readApiArray(value).map((item) => ({
    name: item.name ?? '',
    phone: item.phone ?? '',
    email: item.email,
    status: item.status ?? '',
    deliveryChannel: item.deliveryChannel,
    deliveryWarning: item.deliveryWarning,
    sentAt: item.sentAt,
    viewedAt: item.viewedAt,
    isPrimary: item.isPrimary ?? false,
  }))

const normalizePropertySubmissionItem = (payload: unknown): PropertySubmissionItem => {
  const item = toApiRecord(payload)

  return {
    ...item,
    id: item.id ?? '',
    canSelectForFoundPlace: item.canSelectForFoundPlace === true,
    title: item.title ?? '',
    address: item.address ?? '',
    submittedAt: item.submittedAt,
    agentName: item.agentName ?? '',
    agentPhone: item.agentPhone ?? '',
    agentEmail: item.agentEmail,
    agents: normalizePropertySubmissionAgents(item.agents),
    status: item.status ?? '',
    photoClassName: item.photoClassName ?? '',
    viewedBy: item.viewedBy,
    viewedAt: item.viewedAt,
    expiresAt: item.expiresAt,
    revokedAt: item.revokedAt,
    expiresInDays: toOptionalNumber(item.expiresInDays) ?? 0,
    placeId: item.placeId,
    applicationId: toOptionalNumber(item.applicationId),
    applicationFullName: item.applicationFullName,
    submittedByName: item.submittedByName,
    includedMemberCount: toOptionalNumber(item.includedMemberCount),
    isHouseholdCompositionOutdated: item.isHouseholdCompositionOutdated ?? false,
    householdCompositionChangeType: item.householdCompositionChangeType ?? 'none',
    addedHouseholdMemberNames: normalizeStringArray(item.addedHouseholdMemberNames),
    removedHouseholdMemberNames: normalizeStringArray(item.removedHouseholdMemberNames),
    currentHouseholdMemberCount: toOptionalNumber(item.currentHouseholdMemberCount),
    submittedHouseholdMemberCount: toOptionalNumber(item.submittedHouseholdMemberCount),
  }
}

const readErrorPayload = async (
  response: Response
): Promise<{
  message: string
  code?: string
  isBlocked?: boolean
  blockCode?: string
  blockReasons?: SubmissionBlockReasonResult[]
  redirectUrl?: string
  retryAfterSeconds?: number
}> => {
  try {
    const body = await response.json()
    const message = body?.message
    const code = body?.code ?? body?.errorCode
    const blockCode = body?.blockCode
    const blockReasons = body?.blockReasons ?? body?.submissionGate?.blockReasons
    const normalizedBlockReasons = normalizeSubmissionBlockReasons(blockReasons)
    const isBlocked =
      body?.isBlocked === true ||
      code === 'submission_blocked' ||
      code === 'household_members_not_ready' ||
      Boolean(blockCode) ||
      normalizedBlockReasons.length > 0

    if (typeof message === 'string' && message.trim()) {
      return {
        message,
        redirectUrl: typeof body?.redirectUrl === 'string' ? body.redirectUrl : undefined,
        retryAfterSeconds: typeof body?.retryAfterSeconds === 'number' ? body.retryAfterSeconds : undefined,
        code: typeof code === 'string' && code.trim() ? code : undefined,
        isBlocked,
        blockCode: typeof blockCode === 'string' && blockCode.trim() ? blockCode : undefined,
        blockReasons: normalizedBlockReasons.length > 0 ? normalizedBlockReasons : undefined,
      }
    }

    if (isBlocked) {
      return {
        message: 'This household cannot be submitted yet.',
        code: typeof code === 'string' && code.trim() ? code : undefined,
        isBlocked: true,
        blockCode: typeof blockCode === 'string' && blockCode.trim() ? blockCode : undefined,
        blockReasons: normalizedBlockReasons.length > 0 ? normalizedBlockReasons : undefined,
      }
    }

    const errors = body?.errors
    if (errors && typeof errors === 'object') {
      const firstEntry = Object.values(errors as Record<string, unknown>)[0]
      if (Array.isArray(firstEntry) && firstEntry.length > 0 && typeof firstEntry[0] === 'string') {
        return {message: firstEntry[0]}
      }
    }
  } catch {
    // ignore json parse issues for non-json responses
  }

  return {message: `${response.status} ${response.statusText}`}
}

const readErrorMessage = async (response: Response): Promise<string> => {
  const payload = await readErrorPayload(response)
  return payload.message
}

export async function throwSecureSubmissionError(response: Response): Promise<never> {
  const payload = await readErrorPayload(response)
  const retryHeader = Number(response.headers.get('Retry-After'))
  throw new WizardApiError(payload.message, {
    ...payload,
    status: response.status,
    retryAfterSeconds: payload.retryAfterSeconds ?? (retryHeader > 0 ? retryHeader : undefined),
  })
}

const readAxiosErrorMessage = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    const body = error.response?.data as any
    const message = body?.message ?? body?.detail
    if (typeof message === 'string' && message.trim()) {
      return message
    }

    if (error.response?.status) {
      return `${error.response.status} ${error.response.statusText}`
    }
  }

  return error instanceof Error ? error.message : 'Request failed.'
}

const buildWizardHeaders = (): Record<string, string> => ({
  ...JSON_HEADERS,
  [CORRELATION_ID_HEADER]: getOrCreateWizardCorrelationId(),
})

const buildWizardGetHeaders = (): Record<string, string> => ({
  [CORRELATION_ID_HEADER]: getOrCreateWizardCorrelationId(),
})

const mapPlacePredictions = (predictions: PlacePrediction[]): IAddress[] =>
  predictions.map((prediction) => ({
    id: prediction.placeId,
    address: prediction.description,
  }))

export async function searchAddresses(sessionId: string, query: string): Promise<IAddress[]> {
  const normalizedSessionId = sessionId?.trim() ?? ''
  const normalizedQuery = query.trim()

  if (!normalizedSessionId || normalizedQuery.length < MIN_ADDRESS_SEARCH_LENGTH) {
    return []
  }

  const params = new URLSearchParams({
    sessionId: normalizedSessionId,
    input: normalizedQuery,
  })

  const response = await fetch(`${API_BASE}/maps/autocomplete?${params.toString()}`, {
    credentials: 'include',
    headers: buildWizardGetHeaders(),
  })

  if (!response.ok) {
    throw new Error(`Failed to search addresses: ${await readErrorMessage(response)}`)
  }

  return mapPlacePredictions((await response.json()) as PlacePrediction[])
}

export async function getPlaceDetails(sessionId: string, placeId: string): Promise<IPlaceDetails> {
  const normalizedSessionId = sessionId?.trim() ?? ''
  const normalizedPlaceId = placeId?.trim() ?? ''

  if (!normalizedSessionId) {
    throw new Error('SessionId is required for place details.')
  }

  if (!normalizedPlaceId) {
    throw new Error('PlaceId is required for place details.')
  }

  const cacheKey = `${normalizedSessionId}:${normalizedPlaceId}`
  if (placeDetailsCache.has(cacheKey)) {
    return placeDetailsCache.get(cacheKey)!
  }

  const params = new URLSearchParams({
    sessionId: normalizedSessionId,
    placeId: normalizedPlaceId,
  })

  const response = await fetch(`${API_BASE}/maps/details?${params.toString()}`, {
    credentials: 'include',
    headers: buildWizardGetHeaders(),
  })

  if (!response.ok) {
    throw new Error(`Failed to load place details: ${await readErrorMessage(response)}`)
  }

  const details = (await response.json()) as IPlaceDetails
  placeDetailsCache.set(cacheKey, details)
  return details
}

export async function getStreetViewImageUrl(
  sessionId: string,
  lat: number,
  lng: number,
  options: StreetViewOptions = {}
): Promise<string> {
  const normalizedSessionId = sessionId?.trim() ?? ''

  if (!normalizedSessionId) {
    throw new Error('SessionId is required for street view.')
  }

  const params = new URLSearchParams({
    sessionId: normalizedSessionId,
    lat: String(lat),
    lng: String(lng),
  })

  Object.entries(options).forEach(([key, value]) => {
    if (value !== undefined) {
      params.set(key, String(value))
    }
  })

  const cacheKey = `${normalizedSessionId}:${params.toString()}`
  if (streetViewCache.has(cacheKey)) {
    return streetViewCache.get(cacheKey)!
  }

  const response = await fetch(`${API_BASE}/maps/streetview?${params.toString()}`, {
    credentials: 'include',
    headers: buildWizardGetHeaders(),
  })

  if (!response.ok) {
    throw new Error(`Failed to load street view: ${await readErrorMessage(response)}`)
  }

  const url = URL.createObjectURL(await response.blob())
  streetViewCache.set(cacheKey, url)
  return url
}

export async function saveApplication(
  data: IApplicationFormData,
  options?: {isAutosave?: boolean; stepKey?: string; updateRetentionPreference?: boolean}
): Promise<SaveDraftResult> {
  if (!data.sessionId) {
    throw new Error('SessionId is required for wizard save.')
  }

  const payload: SaveDraftPayload = {
    draft: buildDraftPayload(data, options?.stepKey),
    isAutosave: options?.isAutosave ?? false,
  }
  if (!options?.updateRetentionPreference) {
    delete payload.draft.retentionPreference
  }

  const postDraft = async (): Promise<SaveDraftResult> => {
    const response = await fetch(`${API_BASE}/draft`, {
      method: 'POST',
      headers: buildWizardHeaders(),
      credentials: 'include',
      body: JSON.stringify(payload),
    })
    if (!response.ok) {
      throw new Error(`Failed to save draft: ${await readErrorMessage(response)}`)
    }
    const result = (await response.json()) as SaveDraftResult
    if (payload.draft.stepKey === 'other-occupants') {
      result.otherOccupants = readSavedOtherOccupants(
        result.otherOccupants,
        payload.draft.otherOccupantChanges?.upserts
      )
    }
    return result
  }

  if (payload.draft.stepKey !== 'other-occupants') {
    return postDraft()
  }

  // Keep autosave and Continue in order, including removal of a newly saved entry.
  const previousSave = occupantSavesBySession.get(data.sessionId)
  const save = (previousSave ?? Promise.resolve()).catch(() => undefined).then(postDraft)
  occupantSavesBySession.set(data.sessionId, save)
  try {
    return await save
  } finally {
    if (occupantSavesBySession.get(data.sessionId) === save) {
      occupantSavesBySession.delete(data.sessionId)
    }
  }
}

export class HouseholdWarningRequiredError extends Error {
  readonly redirectTo: string
  constructor(sessionId: string) {
    super('Complete the household warning before continuing to onboarding.')
    this.redirectTo = `/application-wizard/profile?sessionId=${encodeURIComponent(sessionId)}`
  }
}

export type HouseholdJoinWarning = {
  sessionId: string
  primaryRenterFirstName: string
  activeSubmissionCount: number
  step: 'warning-1' | 'warning-2' | 'submitted' | 'cancelled' | 'completed'
  processingState?: 'pending' | 'processing' | 'manual_review' | 'completed' | 'cancelled'
  lastProcessingError?: string
}

export async function getHouseholdJoinWarning(sessionId: string): Promise<HouseholdJoinWarning> {
  const response = await fetch(`${API_BASE}/coapp/join-warning?sessionId=${encodeURIComponent(sessionId)}`, {credentials: 'include'})
  if (!response.ok) throw new Error(await readErrorMessage(response))
  return response.json()
}

export async function advanceHouseholdJoinWarning(sessionId: string, action: 'continue' | 'back' | 'cancel' | 'confirm'): Promise<HouseholdJoinWarning> {
  const response = await fetch(`${API_BASE}/coapp/join-warning`, {
    method: 'POST', headers: JSON_HEADERS, credentials: 'include', body: JSON.stringify({sessionId, action}),
  })
  if (!response.ok) throw new Error(await readErrorMessage(response))
  return response.json()
}

export async function loadApplication(sessionId?: string): Promise<IApplicationFormData> {
  if (!sessionId) {
    return normalizeApplicationFormData()
  }

  const response = await fetch(
    `${API_BASE}/draft?sessionId=${encodeURIComponent(String(sessionId))}`,
    {
      credentials: 'include',
      headers: {
        [CORRELATION_ID_HEADER]: getOrCreateWizardCorrelationId(),
      },
    }
  )
  if (!response.ok) {
    if (response.status === 409) {
      const body = await response.clone().json().catch(() => null)
      if (body?.nextStepPath === 'household-warning') throw new HouseholdWarningRequiredError(sessionId)
    }
    const message = await readErrorMessage(response)
    console.error('[wizard-resume] Load application failed', {
      sessionId,
      status: response.status,
      message,
    })
    throw new Error(`Failed to load draft: ${message}`)
  }

  const payload = (await response.json()) as LoadDraftResult
  if (!payload.found || !payload.draft) {
    console.error('[wizard-resume] Application was not found', {
      sessionId,
      found: payload.found,
    })
    throw new Error('Existing application was not found.')
  }

  const application = mapDraftToForm(payload.draft)
  return application
}

export async function checkDraftExistsByMobile(mobileNumber: string): Promise<VerifyMobileResult> {
  const response = await fetch(
    `${API_BASE}/exists?mobileNumber=${encodeURIComponent(mobileNumber)}`,
    {
      credentials: 'include',
      headers: {
        [CORRELATION_ID_HEADER]: getOrCreateWizardCorrelationId(),
      },
    }
  )

  if (!response.ok) {
    throw new Error(`Failed to check draft: ${await readErrorMessage(response)}`)
  }

  return (await response.json()) as VerifyMobileResult
}

export async function sendWizardOtp(request: {mobileNumber: string}): Promise<SendOtpResult> {
  const response = await fetch(`${API_BASE}/otp/send`, {
    method: 'POST',
    headers: buildWizardHeaders(),
    credentials: 'include',
    body: JSON.stringify(request),
  })

  if (!response.ok) {
    throw new Error(`Failed to send OTP: ${await readErrorMessage(response)}`)
  }

  return (await response.json()) as SendOtpResult
}

export async function verifyWizardOtp(request: {
  mobileNumber: string
  otpCode: string
  action?: 'resume' | 'new'
  fullName?: string
  firstName?: string
  lastName?: string
}): Promise<VerifyOtpResult> {
  const response = await fetch(`${API_BASE}/otp/verify`, {
    method: 'POST',
    headers: buildWizardHeaders(),
    credentials: 'include',
    body: JSON.stringify(request),
  })

  if (!response.ok) {
    throw new Error(`Failed to verify OTP: ${await readErrorMessage(response)}`)
  }

  return (await response.json()) as VerifyOtpResult
}

export async function loadReviewSummary(sessionId: string): Promise<IWizardReviewSummary> {
  const response = await fetch(
    `${API_BASE}/draft/review?sessionId=${encodeURIComponent(String(sessionId))}`,
    {
      credentials: 'include',
      headers: {
        [CORRELATION_ID_HEADER]: getOrCreateWizardCorrelationId(),
      },
    }
  )

  if (!response.ok) {
    throw new Error(`Failed to load review summary: ${await readErrorMessage(response)}`)
  }

  return normalizeReviewSummaryPayload(await response.json())
}

export async function loadHousehold(sessionId: string): Promise<IWizardReviewSummary> {
  const response = await fetch(
    `${API_BASE}/draft/household?sessionId=${encodeURIComponent(String(sessionId))}`,
    {
      credentials: 'include',
      headers: {
        [CORRELATION_ID_HEADER]: getOrCreateWizardCorrelationId(),
      },
    }
  )

  if (!response.ok) {
    throw new Error(`Failed to load household: ${await readErrorMessage(response)}`)
  }

  return normalizeReviewSummaryPayload(await response.json())
}

export async function finalizeWizardDooraCard(request: {
  sessionId: string
  pmEmail?: string
}): Promise<FinalizeDooraCardResult> {
  const response = await fetch(`${API_BASE}/submission/finalizedooracard`, {
    method: 'POST',
    headers: buildWizardHeaders(),
    credentials: 'include',
    body: JSON.stringify(request),
  })

  if (!response.ok) {
    const errorPayload = await readErrorPayload(response)
    throw new WizardApiError(`Failed to finalise submission: ${errorPayload.message}`, {
      code: errorPayload.code,
      status: response.status,
      isBlocked: errorPayload.isBlocked,
      blockCode: errorPayload.blockCode,
      blockReasons: errorPayload.blockReasons,
    })
  }

  return (await response.json()) as FinalizeDooraCardResult
}

export const SUBMISSION_VALIDATION_FIELD_CODES = [
  'propertyAddress',
  'agentName',
  'agentPhone',
  'agentEmail',
] as const

export type SubmissionValidationFieldCode = typeof SUBMISSION_VALIDATION_FIELD_CODES[number]

export async function reportSubmissionValidationFailure(request: {
  sessionId: string
  attemptId: string
  fieldCodes: SubmissionValidationFieldCode[]
}): Promise<void> {
  const response = await fetch(`${API_BASE}/submission/validation-failure`, {
    method: 'POST',
    headers: buildWizardHeaders(),
    credentials: 'include',
    keepalive: true,
    body: JSON.stringify(request),
  })

  if (!response.ok) {
    throw new Error('Unable to record submission validation failure.')
  }
}

export async function submitToProperty(request: {
  sessionId: string
  propertyAddress: string
  propertyPlaceId?: string
  agentName: string
  agentPhone: string
  agentEmail?: string
  agents?: Array<{
    agentName: string
    agentPhone: string
    agentEmail?: string
  }>
}): Promise<SubmitToPropertyResult> {
  const response = await fetch(`${API_BASE}/submission/submit-to-property`, {
    method: 'POST',
    headers: buildWizardHeaders(),
    credentials: 'include',
    body: JSON.stringify(request),
  })

  if (!response.ok) {
    const errorPayload = await readErrorPayload(response)
    throw new WizardApiError(errorPayload.message, {
      code: errorPayload.code,
      status: response.status,
      isBlocked: errorPayload.isBlocked,
      blockCode: errorPayload.blockCode,
      blockReasons: errorPayload.blockReasons,
    })
  }

  return normalizeSubmitToPropertyResult(await response.json())
}

export async function loadPropertySubmissions(
  sessionId: string
): Promise<PropertySubmissionItem[]> {
  const response = await fetch(
    `${API_BASE}/submission/list?sessionId=${encodeURIComponent(sessionId)}`,
    {
      credentials: 'include',
      headers: {
        [CORRELATION_ID_HEADER]: getOrCreateWizardCorrelationId(),
      },
    }
  )

  if (!response.ok) {
    throw new Error(`Failed to load submissions: ${await readErrorMessage(response)}`)
  }

  const payload = await response.json()
  return Array.isArray(payload) ? payload.map(normalizePropertySubmissionItem) : []
}

export async function revokePropertySubmission(request: {
  sessionId: string
  submissionId: string
}): Promise<PropertySubmissionItem> {
  const response = await fetch(
    `${API_BASE}/submission/${encodeURIComponent(request.submissionId)}/revoke`,
    {
      method: 'POST',
      headers: buildWizardHeaders(),
      credentials: 'include',
      body: JSON.stringify({sessionId: request.sessionId}),
    }
  )

  if (!response.ok) {
    throw new Error(`Failed to revoke submission: ${await readErrorMessage(response)}`)
  }

  return normalizePropertySubmissionItem(await response.json())
}

export async function restorePropertySubmission(request: {
  sessionId: string
  submissionId: string
}): Promise<PropertySubmissionItem> {
  const response = await fetch(
    `${API_BASE}/submission/${encodeURIComponent(request.submissionId)}/restore`,
    {
      method: 'POST',
      headers: buildWizardHeaders(),
      credentials: 'include',
      body: JSON.stringify({sessionId: request.sessionId}),
    }
  )

  if (!response.ok) {
    throw new Error(`Failed to restore submission: ${await readErrorMessage(response)}`)
  }

  return normalizePropertySubmissionItem(await response.json())
}

export async function confirmFoundPlace(
  request: FoundPlaceConfirmationRequest & {sessionId: string}
): Promise<void> {
  const response = await fetch(`${API_BASE}/found-place`, {
    method: 'POST',
    headers: buildWizardHeaders(),
    credentials: 'include',
    body: JSON.stringify(request),
  })

  if (!response.ok) {
    throw new Error(await readErrorMessage(response))
  }
}

export async function deleteRenterProfile(sessionId: string): Promise<{
  deleted: boolean
  propertySubmissionsDeleted: number
  storageFileDeleteFailures: number
}> {
  const response = await fetch(`${API_BASE}/profile/delete`, {
    method: 'POST',
    headers: buildWizardHeaders(),
    credentials: 'include',
    body: JSON.stringify({sessionId}),
  })

  if (!response.ok) {
    throw new Error(`Failed to delete Doora Card: ${await readErrorMessage(response)}`)
  }

  return response.json()
}

export async function loadSecureSubmissionView(token: string): Promise<SecureSubmissionViewResult> {
  const response = await fetch(`${API_BASE}/submission/secure-view/${encodeURIComponent(token)}`, {
    cache: 'no-store',
    credentials: 'include',
  })
  if (!response.ok) {
    return throwSecureSubmissionError(response)
  }

  return (await response.json()) as SecureSubmissionViewResult
}

export async function loadSecureSubmissionDocument(
  token: string,
  documentId: string
): Promise<SecureSubmissionDocumentResult> {
  const response = await fetch(
    `${API_BASE}/submission/secure-view/${encodeURIComponent(token)}/document/${encodeURIComponent(
      documentId
    )}`,
    {
      cache: 'no-store',
      credentials: 'include',
    }
  )

  if (!response.ok) {
    return throwSecureSubmissionError(response)
  }

  return {
    blob: await response.blob(),
    contentType: response.headers.get('content-type') || 'application/octet-stream',
  }
}

export async function createSecureSubmissionPreviewSession(
  token: string
): Promise<SecurePreviewSessionResult> {
  const response = await fetch(
    `${API_BASE}/submission/secure-view/${encodeURIComponent(token)}/preview-session`,
    {
      method: 'POST',
      credentials: 'include',
      headers: {
        [CORRELATION_ID_HEADER]: getOrCreateWizardCorrelationId(),
      },
    }
  )

  if (!response.ok) {
    return throwSecureSubmissionError(response)
  }

  return (await response.json()) as SecurePreviewSessionResult
}

export async function refreshSecureSubmissionPreviewSession(
  previewToken: string
): Promise<SecurePreviewSessionResult> {
  const response = await fetch(`${API_BASE}/submission/secure-view/preview-session/refresh`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      [CORRELATION_ID_HEADER]: getOrCreateWizardCorrelationId(),
      Authorization: `Bearer ${previewToken}`,
    },
  })

  if (!response.ok) {
    return throwSecureSubmissionError(response)
  }

  return (await response.json()) as SecurePreviewSessionResult
}

export async function loadSecureSubmissionDocumentByPreviewSession(
  previewToken: string,
  documentId: string
): Promise<SecureSubmissionDocumentResult> {
  const response = await fetch(
    `${API_BASE}/submission/secure-view/document/${encodeURIComponent(documentId)}`,
    {
      cache: 'no-store',
      credentials: 'include',
      headers: {
        [CORRELATION_ID_HEADER]: getOrCreateWizardCorrelationId(),
        Authorization: `Bearer ${previewToken}`,
      },
    }
  )

  if (!response.ok) {
    return throwSecureSubmissionError(response)
  }

  return {
    blob: await response.blob(),
    contentType: response.headers.get('content-type') || 'application/octet-stream',
  }
}

export async function sendCoApplicantInvite(request: {
  sessionId: string
  fullName: string
  mobileNumber: string
  email: string
  role: string
}): Promise<CoApplicantInviteActionResult> {
  const response = await fetch(`${API_BASE}/coapp/invite/send`, {
    method: 'POST',
    headers: JSON_HEADERS,
    credentials: 'include',
    body: JSON.stringify(request),
  })

  if (!response.ok) {
    throw new Error(`Failed to send co-applicant invite: ${await readErrorMessage(response)}`)
  }

  return (await response.json()) as CoApplicantInviteActionResult
}

export async function resendCoApplicantInvite(request: {
  sessionId: string
  inviteId: string
}): Promise<CoApplicantInviteActionResult> {
  const response = await fetch(`${API_BASE}/coapp/invite/resend`, {
    method: 'POST',
    headers: JSON_HEADERS,
    credentials: 'include',
    body: JSON.stringify(request),
  })

  if (!response.ok) {
    throw new Error(`Failed to resend co-applicant invite: ${await readErrorMessage(response)}`)
  }

  return (await response.json()) as CoApplicantInviteActionResult
}

export async function revokeCoApplicantInvite(request: {
  sessionId: string
  inviteId: string
}): Promise<CoApplicantInviteActionResult> {
  const response = await fetch(`${API_BASE}/coapp/invite/revoke`, {
    method: 'POST',
    headers: JSON_HEADERS,
    credentials: 'include',
    body: JSON.stringify(request),
  })

  if (!response.ok) {
    throw new Error(`Failed to revoke co-applicant invite: ${await readErrorMessage(response)}`)
  }

  return completeInviteRemoval(request.sessionId, (await response.json()) as CoApplicantInviteActionResult)
}

export async function deleteCoApplicantInvite(request: {
  sessionId: string
  inviteId: string
}): Promise<CoApplicantInviteActionResult> {
  const response = await fetch(`${API_BASE}/coapp/invite/delete`, {
    method: 'POST',
    headers: JSON_HEADERS,
    credentials: 'include',
    body: JSON.stringify(request),
  })

  if (!response.ok) {
    throw new Error(`Failed to delete co-applicant invite: ${await readErrorMessage(response)}`)
  }

  return completeInviteRemoval(request.sessionId, (await response.json()) as CoApplicantInviteActionResult)
}

export type HouseholdLeaveResult = {
  leaveEventId: string
  processingState: string
  message?: string
}

export async function requestHouseholdLeave(sessionId: string, householdMemberId: string): Promise<HouseholdLeaveResult> {
  const response = await fetch(`${API_BASE}/household/leave`, {
    method: 'POST', headers: JSON_HEADERS, credentials: 'include',
    body: JSON.stringify({sessionId, householdMemberId}),
  })
  if (!response.ok) throw new Error(await readErrorMessage(response))
  return response.json()
}

export async function getHouseholdLeaveResult(sessionId: string, leaveEventId: string, signal?: AbortSignal): Promise<HouseholdLeaveResult> {
  const response = await fetch(`${API_BASE}/household/leave/${encodeURIComponent(leaveEventId)}?sessionId=${encodeURIComponent(sessionId)}`, {
    credentials: 'include', signal,
  })
  if (!response.ok) throw new WizardApiError(await readErrorMessage(response), {status: response.status})
  return response.json()
}

async function completeInviteRemoval(sessionId: string, result: CoApplicantInviteActionResult): Promise<CoApplicantInviteActionResult> {
  if (!result.success || !result.leaveEventId) return result
  for (let attempt = 0; attempt < 60; attempt += 1) {
    const leave = await getHouseholdLeaveResult(sessionId, result.leaveEventId)
    if (leave.processingState === 'completed') return {...result, processingState: 'completed'}
    if (leave.processingState === 'manual_review') return {...result, success: false, message: leave.message || 'This household change needs support review.'}
    await new Promise((resolve) => setTimeout(resolve, 2000))
  }
  return {...result, success: false, message: 'The household change is still processing. Refresh to check its progress.'}
}

export async function verifyCoApplicantInvite(request: {
  inviteId: string
  mobileNumber: string
  otpCode: string
}): Promise<VerifyCoApplicantInviteResult> {
  const response = await fetch(`${API_BASE}/coapp/invite/verify`, {
    method: 'POST',
    headers: JSON_HEADERS,
    credentials: 'include',
    body: JSON.stringify(request),
  })

  if (!response.ok) {
    throw new Error(`Failed to verify co-applicant invite: ${await readErrorMessage(response)}`)
  }

  return (await response.json()) as VerifyCoApplicantInviteResult
}

export async function getCoApplicantInviteStatus(
  inviteId: string
): Promise<CoApplicantInviteStatusResult> {
  const response = await fetch(
    `${API_BASE}/coapp/invite/status?inviteId=${encodeURIComponent(inviteId)}`,
    {
      credentials: 'include',
      headers: {
        [CORRELATION_ID_HEADER]: getOrCreateWizardCorrelationId(),
      },
    }
  )

  if (!response.ok) {
    throw new Error(`Failed to load invite status: ${await readErrorMessage(response)}`)
  }

  return (await response.json()) as CoApplicantInviteStatusResult
}

type SendEmailOtpResult = {
  sent: boolean
  expiresAtUtc?: string
  message?: string
  retryAfterSeconds?: number
  isLockedOut?: boolean
}

type VerifyEmailOtpResult = {
  verified: boolean
  sessionId?: string
  verifiedAtUtc?: string
  failureCode?: string
  retryAfterSeconds?: number
  isLockedOut?: boolean
  requiresNewCode?: boolean
}

export async function sendEmailOtp(request: {email: string}): Promise<SendEmailOtpResult> {
  const response = await fetch(`${API_BASE}/email-otp/send`, {
    method: 'POST',
    headers: buildWizardHeaders(),
    credentials: 'include',
    body: JSON.stringify(request),
  })

  if (!response.ok) {
    throw new Error(`Failed to send email OTP: ${await readErrorMessage(response)}`)
  }

  return (await response.json()) as SendEmailOtpResult
}

export async function verifyEmailOtp(request: {
  email: string
  otpCode: string
  sessionId?: string
}): Promise<VerifyEmailOtpResult> {
  const response = await fetch(`${API_BASE}/email-otp/verify`, {
    method: 'POST',
    headers: buildWizardHeaders(),
    credentials: 'include',
    body: JSON.stringify(request),
  })

  if (!response.ok) {
    throw new Error(`Failed to verify email OTP: ${await readErrorMessage(response)}`)
  }

  return (await response.json()) as VerifyEmailOtpResult
}

export async function uploadWizardDocument(request: {
  sessionId: string
  type: string
  file: File
  documentId?: string
  onProgress?: (progressPercent: number) => void
}): Promise<IApplicationFormData['documents'][number]> {
  const formData = new FormData()
  formData.append('sessionId', request.sessionId)
  formData.append('type', request.type)
  formData.append('file', request.file)
  if (request.documentId) {
    formData.append('documentId', request.documentId)
  }

  const uploadResult = await new Promise<{
    status: number
    statusText: string
    responseText: string
  }>((resolve, reject) => {
    const requestUrl = `${API_BASE}/document/upload`
    const xhr = new XMLHttpRequest()

    xhr.open('POST', requestUrl, true)
    xhr.withCredentials = true
    xhr.setRequestHeader(CORRELATION_ID_HEADER, getOrCreateWizardCorrelationId())

    xhr.upload.onprogress = (event) => {
      if (!event.lengthComputable || !request.onProgress) {
        return
      }

      const progressPercent = Math.min(
        100,
        Math.max(0, Math.round((event.loaded / event.total) * 100))
      )
      request.onProgress(progressPercent)
    }

    xhr.onload = () =>
      resolve({
        status: xhr.status,
        statusText: xhr.statusText,
        responseText: xhr.responseText ?? '',
      })
    xhr.onerror = () => reject(new Error('Failed to upload document: network error.'))
    xhr.onabort = () => reject(new Error('Failed to upload document: request was aborted.'))
    xhr.send(formData)
  })

  if (uploadResult.status < 200 || uploadResult.status >= 300) {
    let errorMessage = `${uploadResult.status} ${uploadResult.statusText}`
    try {
      const body = JSON.parse(uploadResult.responseText)
      const message = body?.message
      if (typeof message === 'string' && message.trim()) {
        errorMessage = message
      }
    } catch {
      // ignore response parse issues for non-json payloads
    }

    throw new Error(`Failed to upload document: ${errorMessage}`)
  }

  let payload: UploadWizardDocumentResponse
  try {
    payload = JSON.parse(uploadResult.responseText) as UploadWizardDocumentResponse
  } catch {
    throw new Error('Failed to upload document: invalid server response.')
  }

  if (!payload.success || !payload.document) {
    throw new Error(payload.message ?? 'Failed to upload document.')
  }

  if (request.onProgress) {
    request.onProgress(100)
  }

  return payload.document
}

export async function deleteWizardDocument(request: {
  sessionId: string
  documentId: string
}): Promise<void> {
  const response = await fetch(`${API_BASE}/document/delete`, {
    method: 'POST',
    headers: buildWizardHeaders(),
    credentials: 'include',
    body: JSON.stringify(request),
  })

  if (!response.ok) {
    throw new Error(`Failed to delete document: ${await readErrorMessage(response)}`)
  }
}

export async function downloadWizardDocument(sessionId: string, documentId: string): Promise<Blob> {
  const response = await fetch(
    `${API_BASE}/document/download?sessionId=${encodeURIComponent(
      sessionId
    )}&documentId=${encodeURIComponent(documentId)}`,
    {
      credentials: 'include',
      headers: {
        [CORRELATION_ID_HEADER]: getOrCreateWizardCorrelationId(),
      },
    }
  )

  if (!response.ok) {
    throw new Error(`Failed to download document: ${await readErrorMessage(response)}`)
  }

  return response.blob()
}

export async function getSubmissionEnterEmailContext(
  token: string
): Promise<SubmissionEnterEmailContext> {
  const response = await fetch(
    `${API_BASE}/submission/secure-view/${encodeURIComponent(token)}/enter-email`,
    {
      cache: 'no-store',
    }
  )

  if (!response.ok) {
    throw new Error(`${await readErrorMessage(response)}`)
  }

  return (await response.json()) as SubmissionEnterEmailContext
}

export async function submitPmEmail(token: string, email: string): Promise<SubmitPmEmailResult> {
  const response = await fetch(
    `${API_BASE}/submission/secure-view/${encodeURIComponent(token)}/enter-email`,
    {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify({email}),
    }
  )

  if (!response.ok) {
    throw new Error(`${await readErrorMessage(response)}`)
  }

  return (await response.json()) as SubmitPmEmailResult
}

export async function getDooraAdminEnterEmailContext(
  token: string
): Promise<SubmissionEnterEmailContext> {
  try {
    const {data} = await axios.get<SubmissionEnterEmailContext>(
      `${API_BASE}/submission/secure-view/${encodeURIComponent(token)}/admin-enter-email`
    )
    return data
  } catch (error) {
    throw new Error(readAxiosErrorMessage(error))
  }
}

export async function submitDooraAdminAgentEmail(
  token: string,
  email: string
): Promise<SubmitPmEmailResult> {
  try {
    const {data} = await axios.post<SubmitPmEmailResult>(
      `${API_BASE}/submission/secure-view/${encodeURIComponent(token)}/admin-enter-email`,
      {email}
    )
    return data
  } catch (error) {
    throw new Error(readAxiosErrorMessage(error))
  }
}

export type EmailUnsubscribeContext = {
  firstName?: string
  isAlreadyOptedOut: boolean
}

export type EmailUnsubscribeResult = {
  status: 'success' | 'already-opted-out'
}

export async function getEmailUnsubscribeContext(token: string): Promise<EmailUnsubscribeContext> {
  const response = await fetch(`${API_BASE}/unsubscribe/${encodeURIComponent(token)}`)

  if (!response.ok) {
    throw new Error(`${await readErrorMessage(response)}`)
  }

  return (await response.json()) as EmailUnsubscribeContext
}

export async function submitEmailUnsubscribe(token: string): Promise<EmailUnsubscribeResult> {
  const response = await fetch(`${API_BASE}/unsubscribe/${encodeURIComponent(token)}`, {
    method: 'POST',
  })

  if (!response.ok) {
    throw new Error(`${await readErrorMessage(response)}`)
  }

  return (await response.json()) as EmailUnsubscribeResult
}

export async function sendCoApplicantInviteOtp(request: {
    inviteId: string,
    mobileNumber: string
}): Promise<CoApplicantInviteActionResult> {
    const response = await fetch(`${API_BASE}/coapp/invite/otp/send`, {
        method: 'POST',
        headers: JSON_HEADERS,
        credentials: 'include',
        body: JSON.stringify(request)
    })

    if (!response.ok) {
        throw new Error(`Failed to send co-applicant OTP: ${await readErrorMessage(response)}`)
    }

    return (await response.json()) as CoApplicantInviteActionResult
}
export async function getCoApplicantPrimaryApplicant(
    sessionId: string,
): Promise<IApplicationFormData> {
    if (!sessionId) {
        return normalizeApplicationFormData()
    }

    const response = await fetch(
        `${API_BASE}/coapp/primaryApplicant?sessionId=${encodeURIComponent(String(sessionId))}`,
        {
            credentials: 'include',
            headers: {
                [CORRELATION_ID_HEADER]: getOrCreateWizardCorrelationId(),
            },
        }
    )
    if (!response.ok) {
        const message = await readErrorMessage(response)
        throw new Error(`Failed to load primary applicant: ${message}`)
    }

    const payload = (await response.json()) as LoadDraftResult
    if (!payload.found || !payload.draft) {
        throw new Error('Existing application was not found.')
    }

    const application = mapDraftToForm(payload.draft)
    return application;
}
export async function coApplicantDeclineInvitation(request: {
    inviteId: string
}): Promise<CoApplicantInviteActionResult> {
    const response = await fetch(`${API_BASE}/coapp/invite/decline`, {
        method: 'POST',
        headers: JSON_HEADERS,
        credentials: 'include',
        body: JSON.stringify(request),
    })

    if (!response.ok) {
        throw new Error(`Failed to decline co-applicant invite: ${await readErrorMessage(response)}`)
    }

    return (await response.json()) as CoApplicantInviteActionResult
}
